import React, { useState, useEffect } from "react";
import { Upload, Typography, message, Spin, Progress } from "antd";
import { PlusOutlined, CheckCircleOutlined, ExclamationCircleOutlined, CloseOutlined, LoadingOutlined, FileOutlined, PlayCircleOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { useParams } from "react-router-dom";
import { getAttachmentConfig, uploadFile, uploadAttachment, deleteAttachment } from "@ifa/utils";
import type { OrderAttachmentConfig, AttachmentItem, OrderAttachmentDTO } from "@ifa/utils";
import { FilePreviewModal, getFilePreviewInfo, detectFileType, FileType } from "../../components/FilePreviewModal";
import { useIOSConfirm } from "../../hooks/useIOSConfirm";
import { useImageCompression } from "@ifa/ui";
import styles from "./OrderAttachmentForm.module.css";

import pdfIcon from "../../assets/img/pdf.png";
import excelIcon from "../../assets/img/excel.png";
import wordIcon from "../../assets/img/word.png";

import type { Request as OrderVO } from "./orders";

const { Title, Text } = Typography;
interface OrderAttachmentFormProps {
  orderData?: OrderVO;
  onChange?: (files: any) => void;
  readOnly?: boolean;
}

export const OrderAttachmentForm: React.FC<OrderAttachmentFormProps> = ({ orderData, onChange, readOnly = false }) => {
  const { orderId } = useParams();
  const { confirm } = useIOSConfirm();
  const [currentCategoryKey, setCurrentCategoryKey] = useState<string>("");
  // State to hold the configurations (Categories)
  const [categories, setCategories] = useState<OrderAttachmentConfig[]>([]);

  // Preview Modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string; fileType: FileType }>();

  // 上传中的文件状态 Map: `${categoryType}-${itemIndex}` => uploading file info
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, { uid: string; name: string; percent?: number }>>(new Map());

  // 使用图片压缩 Hook
  const { handleBeforeUpload } = useImageCompression({
    confirm,
  });

  // Fetch Categories based on Order ID
  const fetchConfig = async () => {
    if (!orderId) return;

    try {
      const numericOrderId = parseInt(orderId, 10);
      const config = await getAttachmentConfig(numericOrderId);

      // Map backend files to UploadFile objects if necessary
      // We iterate and ensure files have uid, status, and signed url
      const processedConfig = await Promise.all(
        (config || []).map(async (cat) => ({
          ...cat,
          items: await Promise.all(
            (cat.items || []).map(async (item) => ({
              ...item,
              files: await Promise.all(
                (item.files || []).map(async (f: any, i: number) => {
                  const path = typeof f === "string" ? f : f.path || f.url;
                  const ossPath = typeof f === "object" ? f.ossPath : undefined;
                  // Use path directly as URL for display (may be signed URL)
                  const signedUrl = path;

                  return {
                    uid: f.attachmentId ? String(f.attachmentId) : typeof f === "object" && f.uid ? f.uid : `-${i}`,
                    name: typeof f === "object" && f.name ? f.name : path ? path.split("/").pop() : "unnamed",
                    status: "done",
                    url: signedUrl,
                    path: path, // Store path
                    ossPath: ossPath, // Store ossPath for API requests
                    attachmentId: f.attachmentId, // Ensure attachmentId is preserved
                    response: path, // Store path for deletion
                    ...(typeof f === "object" ? f : {}),
                  };
                })
              ),
            }))
          ),
        }))
      );

      setCategories(processedConfig);
      onChange?.(processedConfig);

      if (processedConfig && processedConfig.length > 0 && !currentCategoryKey) {
        setCurrentCategoryKey(processedConfig[0].type || "");
      }
    } catch (error) {
      console.error("Failed to fetch attachment config:", error);
      message.error("獲取附件配置失敗");
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [orderId]);

  const handlePreview = async (file: UploadFile) => {
    // 优先使用 ossPath，如果没有则使用 path
    const filePath = (file as any).ossPath || (file as any).path || file.response || file.url;

    if (!filePath) {
      message.error("檔案路徑不可用");
      return;
    }

    const fileName = file.name || "unnamed";
    const fileType = detectFileType(filePath, fileName);

    // 对于本地新上传的文件（有 originFileObj），使用 FileReader 读取
    if (file.originFileObj && !file.url) {
      const previewUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj as Blob);
        reader.onload = () => resolve(reader.result as string);
      });
      setPreviewFile({ url: previewUrl, name: fileName, fileType });
      setPreviewOpen(true);
      return;
    }

    // 其他情况：传递 ossPath/path 给 FilePreviewModal，让它通过 API 获取
    setPreviewFile({ url: filePath, name: fileName, fileType });
    setPreviewOpen(true);
  };

  // Handle immediate upload
  const handleCustomRequest = async (options: any, item: AttachmentItem, categoryType: string, itemIndex: number) => {
    const { onSuccess, onError, file } = options;

    if (!orderId) {
      message.error("訂單ID不存在");
      onError(new Error("No Order ID"));
      return;
    }

    // 使用 Hook 处理图片压缩
    const fileToUpload = await handleBeforeUpload(file);
    if (fileToUpload === false) {
      // 用户取消上传
      onError(new Error("User cancelled"));
      return;
    }

    const uploadKey = `${categoryType}-${itemIndex}`;
    const fileUid = `uploading-${Date.now()}`;

    // 添加上传中状态
    setUploadingFiles((prev) => {
      const newMap = new Map(prev);
      newMap.set(uploadKey, { uid: fileUid, name: file.name, percent: 0 });
      return newMap;
    });

    try {
      // 1. Upload File to Object Storage
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("module", "ORDER");
      formData.append("key", orderData.orderNo || "");

      const filePath = await uploadFile(formData);

      // 更新进度到 50%
      setUploadingFiles((prev) => {
        const newMap = new Map(prev);
        newMap.set(uploadKey, { uid: fileUid, name: file.name, percent: 50 });
        return newMap;
      });

      // 2. Link Attachment to Order
      const uploadData: OrderAttachmentDTO = {
        orderId: orderId,
        token: filePath,
        type: item.type!,
      };

      await uploadAttachment(uploadData);

      // 更新进度到 80%
      setUploadingFiles((prev) => {
        const newMap = new Map(prev);
        newMap.set(uploadKey, { uid: fileUid, name: file.name, percent: 80 });
        return newMap;
      });

      // Success
      message.success(`上載成功`);
      onSuccess(filePath, file);

      // 刷新配置以获取真实的回显数据
      await fetchConfig();

      // 移除上传中状态
      setUploadingFiles((prev) => {
        const newMap = new Map(prev);
        newMap.delete(uploadKey);
        return newMap;
      });
    } catch (err) {
      console.error(err);
      message.error(`${file.name} 上載失敗`);

      // 移除上传中状态
      setUploadingFiles((prev) => {
        const newMap = new Map(prev);
        newMap.delete(uploadKey);
        return newMap;
      });

      onError(err);
    }
  };

  const handleRemove = async (file: UploadFile) => {
    if (!orderId) return false;

    // 使用 iOS 风格确认弹窗
    return new Promise<boolean>((resolve) => {
      confirm({
        title: "確認刪除",
        content: `確定要刪除檔案 "${file.name}" 嗎？`,
        okText: "刪除",
        cancelText: "取消",
        onOk: async () => {
          // Try to find attachmentId first
          const attachmentId = (file as any).attachmentId;

          if (attachmentId) {
            try {
              await deleteAttachment(parseInt(orderId, 10), attachmentId);
              message.success("刪除成功");
              // Refresh list
              fetchConfig();
              resolve(true);
            } catch (err) {
              console.error("Delete attachment failed:", err);
              message.error("刪除失敗");
              resolve(false);
            }
          } else {
            // Fallback or local file removal
            resolve(true);
          }
        },
        onCancel: () => {
          resolve(false);
        },
      });
    });
  };

  // Determine status of a category
  const getCategoryStatus = (cat: OrderAttachmentConfig) => {
    if (!cat.required) {
      const hasFiles = cat.items?.some((item) => item.files && item.files.length > 0);
      return hasFiles ? "done" : "optional";
    }

    // If required, check if ALL required items have files
    const allRequiredItemsFilled = cat.items?.every((item) => {
      if (!item.required) return true;
      // Check for files with status 'done' or just existence
      const validFiles = item.files?.filter((f) => f.status === "done" || !f.status);
      return validFiles && validFiles.length > 0;
    });

    return allRequiredItemsFilled ? "done" : "warn";
  };

  const renderSidebarItem = (cat: OrderAttachmentConfig) => {
    const isSelected = currentCategoryKey === cat.type;
    const status = getCategoryStatus(cat);

    let Icon = null;
    let iconColor = "";

    if (status === "done") {
      Icon = CheckCircleOutlined;
      iconColor = "#00C7B6";
    } else if (status === "warn") {
      Icon = ExclamationCircleOutlined;
      iconColor = "#faad14";
    }

    return (
      <div key={cat.type} className={`${styles.sidebarItem} ${isSelected ? styles.active : ""}`} onClick={() => setCurrentCategoryKey(cat.type!)}>
        <span>{cat.name}</span>
        {Icon && <Icon style={{ color: iconColor, fontSize: 20 }} />}
      </div>
    );
  };

  const activeCategory = categories.find((c) => c.type === currentCategoryKey);

  return (
    <div className={styles.container}>
      {/* Left Sidebar */}
      <div className={styles.sidebar}>{categories.map(renderSidebarItem)}</div>

      {/* Right Content */}
      <div className={styles.customUploadWrapper + " " + styles.content}>
        {activeCategory && (
          <>
            <div className={styles.contentHeader}>
              <Title level={4} style={{ margin: 0 }}>
                {activeCategory.name}
              </Title>
              {activeCategory.condition && <Text type="secondary">{activeCategory.condition}</Text>}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {activeCategory.items?.map((item, index) => {
                const uploadKey = `${activeCategory.type}-${index}`;
                const uploadingFile = uploadingFiles.get(uploadKey);

                return (
                  <div key={index} className={styles.uploadItemContainer}>
                    {/* 已上传文件的自定义渲染区域 */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {item.files?.map((file) => {
                        const fileName = file.name?.toLowerCase() || "";
                        let iconSrc = null;
                        let isImage = false;
                        let isVideo = false;

                        if (fileName.endsWith(".pdf")) {
                          iconSrc = pdfIcon;
                        } else if (/\.(xls|xlsx|csv)$/.test(fileName)) {
                          iconSrc = excelIcon;
                        } else if (/\.(doc|docx)$/.test(fileName)) {
                          iconSrc = wordIcon;
                        } else if (/\.(mp4|mov|avi|wmv|flv|mkv|webm|m4v)$/i.test(fileName)) {
                          // 视频格式
                          isVideo = true;
                        } else if (/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileName)) {
                          // 图片格式
                          isImage = true;
                        }

                        return (
                          <div
                            key={file.uid}
                            style={{
                              position: "relative",
                              display: "inline-block",
                              width: 160,
                              height: 110,
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                padding: 8,
                                border: "1px solid #d9d9d9",
                                borderRadius: 8,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                overflow: "hidden",
                                position: "relative",
                                backgroundColor: isVideo ? "#f5f5f5" : "transparent",
                              }}
                              onClick={() => handlePreview(file)}
                            >
                              {iconSrc ? (
                                <img src={iconSrc} alt={file.name} style={{ width: 60, height: 60, objectFit: "contain" }} />
                              ) : isVideo ? (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    height: "100%",
                                  }}
                                >
                                  <PlayCircleOutlined style={{ fontSize: 48, color: "#1890ff" }} />
                                  {/* <div style={{ marginTop: 8, fontSize: 12, color: "#666", textAlign: "center", wordBreak: "break-all", padding: "0 4px" }}>{file.name}</div> */}
                                </div>
                              ) : isImage ? (
                                <img src={file.thumbUrl || file.url || ""} alt={file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                // 未知格式，显示文件名
                                <div style={{ fontSize: 12, color: "#666", textAlign: "center", wordBreak: "break-all", padding: "0 8px" }}>{file.name}</div>
                              )}
                            </div>
                            {!readOnly && (
                              <div
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await handleRemove(file);
                                }}
                                style={{
                                  position: "absolute",
                                  top: -10,
                                  right: -10,
                                  background: "#ff4d4f",
                                  color: "white",
                                  borderRadius: "50%",
                                  width: 24,
                                  height: 24,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  zIndex: 10,
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                }}
                              >
                                <CloseOutlined style={{ fontSize: 14 }} />
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* 上传中的加载动画 */}
                      {uploadingFile && (
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                            width: 160,
                            height: 110,
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              padding: 8,
                              border: "1px solid #d9d9d9",
                              borderRadius: 8,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#fafafa",
                              position: "relative",
                            }}
                          >
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: "#1890ff" }} spin />} />
                            <div style={{ marginTop: 12, fontSize: 12, color: "#666", textAlign: "center" }}>上載中...</div>
                            {uploadingFile.percent !== undefined && <Progress percent={uploadingFile.percent} size="small" style={{ width: "80%", marginTop: 8 }} showInfo={false} />}
                          </div>
                        </div>
                      )}

                      {/* Upload 组件仅用于上传新文件,不传入 fileList */}
                      {!readOnly && (!item.files || item.files.length < (item.max || 1)) && !uploadingFile && (
                        <Upload
                          listType="picture-card"
                          fileList={[]} // 关键:不传入已有文件,避免动画
                          customRequest={(options) => handleCustomRequest(options, item, activeCategory.type!, index)}
                          maxCount={1}
                          disabled={readOnly}
                          showUploadList={false} // 关键:不显示内置的文件列表
                        >
                          <div>
                            <PlusOutlined style={{ fontSize: 24, color: "#999" }} />
                            <div style={{ marginTop: 8, color: "#999" }}>上載</div>
                          </div>
                        </Upload>
                      )}
                    </div>
                    <div className={styles.uploadLabel}>
                      {item.required && <span className={styles.requiredMark}>*</span>}
                      {item.name}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} fileUrl={previewFile?.url} fileName={previewFile?.name} fileType={previewFile?.fileType} />
    </div>
  );
};
